/**
 * FilterSection Component
 * Filter dropdowns for calendar events
 * Collapsible on mobile for better UX
 */

import React, { useCallback } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';

import { useCalendarUI, useCalendarState, useCalendarActions } from 'modules/calendarv2/context/CalendarContext';
import { CATEGORY_OPTIONS, FILTER_TYPES } from '../../constants/filterOptions';
import LocationSelect from 'components/UI/LocationSelect/LocationSelect';
import PracticeAreaSelect from 'components/UI/PracticeAreaSelect/PracticeAreaSelect';
import EventTypeSelect from 'components/UI/EventTypeSelect/EventTypeSelect';
import ConferenceRoomSelect from 'components/UI/ConferenceRoomSelect/ConferenceRoomSelect';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';
import '../../../../assets/scss/FilterResponsive.scss';

const FilterSection = () => {
  const { filters, isGlobal } = useCalendarState();
  const { updateFilter, clearFilters } = useCalendarActions();
  const { openEventFormModal, openSettingsModal } = useCalendarUI();

  const categoryValue = filters[FILTER_TYPES.CATEGORY];
  const isTaskOrSol = categoryValue === 'task' || categoryValue === 'case';
  const isDob = categoryValue === 'user';

  const handleFilterChange = useCallback(
    (filterType, value) => {
      updateFilter(filterType, value);
    },
    [updateFilter]
  );

  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="col-12">
      <div className="d-block d-lg-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="filter-wrap mb-3 p-2">
          <div className="d-block d-lg-none w-100">
            <Button
              className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
              aria-controls="calendar-filter-section"
              label="Filters"
            >
              <span
                style={{
                  fontSize: 18,
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                  transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <i className="fa fa-solid fa-angle-down"></i>
              </span>
            </Button>
          </div>
          <div
            className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
            id="calendar-filter-section"
            aria-hidden={!showFilters}
          >
            <span className="text-bold black-600  me-2 filter-label">Filters:</span>
            <div className="d-flex flex-column  filter-wraper">
              <Dropdown
                value={filters[FILTER_TYPES.CATEGORY]}
                options={CATEGORY_OPTIONS}
                onChange={(e) => handleFilterChange(FILTER_TYPES.CATEGORY, e.value)}
                placeholder="By Category"
                className="w-100 m-0"
              />
            </div>

            <div className="d-flex flex-column  filter-wraper">
              <LocationSelect
                value={filters[FILTER_TYPES.LOCATION]}
                onChange={(value) => handleFilterChange(FILTER_TYPES.LOCATION, value || '')}
                placeholder="By Location"
                className="w-100"
                isDisabled={isTaskOrSol || isDob}
                isClearable={!!filters[FILTER_TYPES.LOCATION]}
                isSearchable={false}
              />
            </div>

            {isGlobal && (
              <div className="d-flex flex-column  filter-wraper">
                <PracticeAreaSelect
                  value={filters[FILTER_TYPES.PRACTICE_AREA]}
                  onChange={(value) => handleFilterChange(FILTER_TYPES.PRACTICE_AREA, value || '')}
                  placeholder="By Practice Area"
                  className="w-100"
                  isDisabled={isDob}
                  isClearable={!!filters[FILTER_TYPES.PRACTICE_AREA]}
                  isSearchable={false}
                />
              </div>
            )}

            <div className="d-flex flex-column  filter-wraper">
              <EventTypeSelect
                value={filters[FILTER_TYPES.EVENT_TYPE]}
                onChange={(value) => handleFilterChange(FILTER_TYPES.EVENT_TYPE, value || '')}
                placeholder="By Type"
                className="w-100"
                isDisabled={isTaskOrSol || isDob}
                isClearable={!!filters[FILTER_TYPES.EVENT_TYPE]}
                isSearchable={false}
                showQuickAdd={false}
              />
            </div>

            {isGlobal && (
              <div className="d-flex flex-column  filter-wraper">
                <ConferenceRoomSelect
                  value={filters[FILTER_TYPES.CONFERENCE_ROOM]}
                  onChange={(value) => handleFilterChange(FILTER_TYPES.CONFERENCE_ROOM, value || '')}
                  placeholder="By Room"
                  className="w-100"
                  isDisabled={isTaskOrSol || isDob}
                  isClearable={!!filters[FILTER_TYPES.CONFERENCE_ROOM]}
                  isSearchable={false}
                  officeLocationId={filters[FILTER_TYPES.LOCATION] || ''}
                />
              </div>
            )}

            {isGlobal && (
              <div className="d-flex flex-column  filter-wraper">
                <div className="d-flex align-items-center gap-2">
                  <span>Show Firm Events</span>
                  <InputSwitch
                    inputId="show-firm-events"
                    checked={Boolean(filters[FILTER_TYPES.FIRM_SCOPE])}
                    onChange={(e) => handleFilterChange(FILTER_TYPES.FIRM_SCOPE, e.value)}
                    disabled={isTaskOrSol || isDob}
                  />
                </div>
              </div>
            )}

            <Button className="p-button-rounded p-button-text" icon="fas fa-sync-alt" onClick={clearFilters} tooltip="Reset Filter" />
          </div>
        </div>
        {/* Desktop: flex row */}
        {!isGlobal && (
          <div className="calendar-v2-header ms-auto d-none d-lg-flex">
            <div className="header-right me-2 w-100 justify-content-end">
              <Button
                label="Create Event"
                icon="pi pi-plus"
                onClick={() => openEventFormModal('create')}
                className="p-button p-button-primary"
              />
              <div className="list-view gear-icon cal-gear" onClick={openSettingsModal}>
                <i className="icon-Setting cursor-pointer"></i>
              </div>
            </div>
          </div>
        )}
        {/* Mobile: stacked below filters */}
        {!isGlobal && (
          <div className="calendar-v2-header mt-2 d-block d-lg-none w-100">
            <div className="header-right w-100 d-flex justify-content-end gap-2">
              <Button
                label="Create Event"
                icon="pi pi-plus"
                onClick={() => openEventFormModal('create')}
                className="p-button p-button-primary"
              />
              <div className="list-view gear-icon cal-gear" onClick={openSettingsModal}>
                <i className="icon-Setting cursor-pointer"></i>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(FilterSection);
